/**
 * Express Portals — Fleet View (Phase 1, read-only).
 *
 * All Clinics shows every Core clinic's portal health at once. A clinic
 * selected in the header only gets a placeholder here — the per-clinic view
 * is Phase 2.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { RefreshCw, Loader2 } from 'lucide-react'
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Badge, Input, Checkbox,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui'
import ClinicDetailView from '../components/express-portals/ClinicDetailView'
import { expressPortalsAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'

const EXPRESS_PORTAL_REPO_URL = 'https://github.com/5axishealth/express-portal'

const FILTERS = ['All', 'Missing portal', 'Deploying', 'Failed', 'Configuration issues', 'Unknown']

/** Rollout monitoring/dispatch status -> the label shown in the outcomes grid.
 *  Kept textually identical to ClinicDetailView.jsx's copy — see Phase 4 spec. */
const ROLLOUT_STATUS_LABEL = {
  accepted: 'Accepted',
  running: 'Running',
  ready: 'Successful',
  errored: 'Failed (errored)',
  canceled: 'Failed (canceled)',
  'timed-out': 'Timed out',
  blocked: 'Blocked',
  'submission-failed': 'Submission failed',
}

const ROLLOUT_POLL_INTERVAL_MS = 2000

/**
 * Consecutive failed status checks before the monitor stops asking.
 *
 * Without a ceiling a failing poll never ends: the results it could not refresh
 * are still "running", so the loop re-arms on every error and keeps asking at
 * full rate for as long as the page is open — which is exactly the traffic that
 * would have caused a rate-limit refusal in the first place.
 */
const ROLLOUT_POLL_MAX_FAILURES = 5

/** Back off between failures so a struggling monitor stops adding to the pile. */
function rolloutPollDelay(consecutiveFailures) {
  return ROLLOUT_POLL_INTERVAL_MS * 2 ** Math.min(consecutiveFailures, ROLLOUT_POLL_MAX_FAILURES)
}

function isRolloutOutcomeActive(status) {
  return status === 'accepted' || status === 'running'
}

/**
 * Merge a status poll's results into the outcomes currently shown, matching
 * on `deploymentId` only — the stale-poller guard on `rolloutId` is checked by
 * the caller before this runs. Entries with no `deploymentId` (blocked /
 * submission-failed from the dispatch response) are left untouched: they were
 * never dispatched, so status polling has nothing to say about them.
 */
function mergeRolloutResults(previousResults, polledResults) {
  const polledByDeploymentId = new Map(polledResults.map((result) => [result.deploymentId, result]))
  return previousResults.map((result) =>
    result.deploymentId && polledByDeploymentId.has(result.deploymentId)
      ? polledByDeploymentId.get(result.deploymentId)
      : result
  )
}

const SUMMARY_BATCH_SIZE = 10
const SUMMARY_BATCH_WORKERS = 2

const HEALTH_LABEL = {
  healthy: 'Healthy',
  warning: 'Warning',
  incomplete: 'Incomplete',
  unavailable: 'Unavailable',
}

const HEALTH_PILL_STYLE = {
  healthy: { variant: 'success', className: '' },
  warning: { variant: 'warning', className: '' },
  incomplete: { variant: 'outline', className: 'border-transparent bg-orange-500 text-white hover:bg-orange-600' },
  unavailable: { variant: 'secondary', className: '' },
}

/** Matches connection.js:133's rule — reimplemented locally per ORCH-CONSTRAINTS §3. */
function coreNameToSlug(name) {
  return name.toLowerCase().replace(/_/g, '-')
}

function initials(text) {
  const trimmed = (text || '').trim()
  if (!trimmed) return '?'
  const parts = trimmed.split(/\s+/)
  return parts.length === 1
    ? parts[0].slice(0, 2).toUpperCase()
    : (parts[0][0] + parts[1][0]).toUpperCase()
}

function formatUtc(iso) {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return date.toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, ' UTC')
}

function apiErrorMessage(err, fallback) {
  return err?.response?.data?.error?.message || fallback
}

function hasVercelError(summary) {
  return (summary?.errors || []).some((error) => error.provider === 'vercel')
}

/** "No project matched" degrades to exactly this: nothing live, and nothing said why. */
function isMissingPortal(summary) {
  return !summary?.activeDeployment && !hasVercelError(summary)
}

function metadataIssueCount(metadataHealth) {
  return (metadataHealth?.checks || [])
    .filter((check) => check.state !== 'healthy')
    .reduce((sum, check) => sum + Math.max(1, (check.missingFields || []).length), 0)
}

function environmentIssueCount(environmentHealth) {
  return (environmentHealth?.missingRequiredKeys?.length || 0) + (environmentHealth?.targetScopeIssues?.length || 0)
}

function matchesFilter(filter, row) {
  if (filter === 'All') return true

  if (filter === 'Unknown') {
    if (row.status === 'error') return true
    if (!row.summary) return false
    return (
      row.summary.metadataHealth?.state === 'unavailable' ||
      row.summary.environmentHealth?.state === 'unavailable' ||
      (row.summary.errors?.length || 0) > 0
    )
  }

  if (row.status !== 'ready' || !row.summary) return false
  const { summary } = row

  switch (filter) {
    case 'Missing portal':
      return isMissingPortal(summary)
    case 'Deploying':
      return summary.newerAttempt?.state === 'queued' || summary.newerAttempt?.state === 'building'
    case 'Failed':
      return summary.newerAttempt?.state === 'failed'
    case 'Configuration issues':
      return (
        ['warning', 'incomplete'].includes(summary.metadataHealth?.state) ||
        ['warning', 'incomplete'].includes(summary.environmentHealth?.state)
      )
    default:
      return true
  }
}

function HealthPill({ state }) {
  const style = HEALTH_PILL_STYLE[state] || HEALTH_PILL_STYLE.unavailable
  return (
    <Badge variant={style.variant} className={style.className}>
      {HEALTH_LABEL[state] || 'Unavailable'}
    </Badge>
  )
}

function ClinicAvatar({ logoUrl, label }) {
  const [failed, setFailed] = useState(false)
  if (!logoUrl || failed) {
    return (
      <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground shrink-0">
        {initials(label)}
      </div>
    )
  }
  return (
    <img
      src={logoUrl}
      alt={label}
      className="w-8 h-8 rounded-full object-cover shrink-0 bg-muted"
      onError={() => setFailed(true)}
    />
  )
}

function ClinicCell({ fleetClinic, summary }) {
  const { clinics, switchClinic } = useAuthStore()
  const slug = coreNameToSlug(fleetClinic.name)
  const canSwitch = clinics.some((clinic) => clinic.id === slug)
  const label = summary?.brandName || fleetClinic.name

  return (
    <div className="flex items-center gap-2">
      <ClinicAvatar logoUrl={summary?.logoUrl} label={label} />
      <div className="min-w-0">
        {canSwitch ? (
          <button
            className="font-medium text-primary hover:underline text-left"
            onClick={() => switchClinic(slug)}
          >
            {fleetClinic.name}
          </button>
        ) : (
          <span className="font-medium">{fleetClinic.name}</span>
        )}
        <div className="text-xs text-muted-foreground">Core: {fleetClinic.status}</div>
      </div>
    </div>
  )
}

function CellSkeleton() {
  return <div className="h-4 bg-muted rounded animate-pulse w-24" />
}

function MetadataCell({ row }) {
  if (row.status === 'loading') return <CellSkeleton />
  if (row.status === 'error') return <span className="text-xs text-destructive">Unavailable</span>

  const { metadataHealth, brandName } = row.summary
  const issues = metadataIssueCount(metadataHealth)

  return (
    <div className="space-y-1">
      <HealthPill state={metadataHealth?.state} />
      {metadataHealth?.state !== 'healthy' && (
        <div className="text-xs text-muted-foreground">{issues} issue{issues === 1 ? '' : 's'}</div>
      )}
      {brandName && <div className="text-xs text-muted-foreground">{brandName}</div>}
    </div>
  )
}

function EnvironmentCell({ row }) {
  if (row.status === 'loading') return <CellSkeleton />
  if (row.status === 'error') return <span className="text-xs text-destructive">Unavailable</span>

  const { environmentHealth } = row.summary
  const issues = environmentIssueCount(environmentHealth)

  return (
    <div className="space-y-1">
      <HealthPill state={environmentHealth?.state} />
      {environmentHealth?.state !== 'healthy' && (
        <div className="text-xs text-muted-foreground">{issues} issue{issues === 1 ? '' : 's'}</div>
      )}
    </div>
  )
}

function DeploymentCell({ row, onRetry, retrying }) {
  if (row.status === 'loading') return <CellSkeleton />

  if (row.status === 'error') {
    return (
      <div className="space-y-1">
        <span className="text-xs text-destructive">{row.error || 'Failed to load'}</span>
        <div>
          <Button size="sm" variant="outline" onClick={onRetry} disabled={retrying}>
            {retrying ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Retry'}
          </Button>
        </div>
      </div>
    )
  }

  const { summary } = row
  const missing = isMissingPortal(summary)
  const activeCommit = summary.activeDeployment?.commit

  return (
    <div className="space-y-1">
      <div className="font-medium">
        {missing ? <span className="text-muted-foreground">Missing project</span> : summary.project}
      </div>

      {summary.activeDeployment && (
        <div className="text-xs">
          {activeCommit?.sha ? (
            <a
              href={`${EXPRESS_PORTAL_REPO_URL}/commit/${activeCommit.sha}`}
              target="_blank"
              rel="noreferrer"
              className="text-primary hover:underline"
            >
              {summary.activeDeployment.branch} · {activeCommit.sha.slice(0, 7)}
            </a>
          ) : (
            <span>{summary.activeDeployment.branch}</span>
          )}
          <div className="text-muted-foreground">
            {[activeCommit?.author, formatUtc(activeCommit?.committedAt)].filter(Boolean).join(' · ')}
          </div>
        </div>
      )}

      {summary.targetCommit?.sha && (
        <div className="text-xs text-muted-foreground">
          Target: {summary.branch} · {summary.targetCommit.sha.slice(0, 7)}
        </div>
      )}

      {summary.activeDeployment?.url && (
        <a
          href={summary.activeDeployment.url}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-primary hover:underline block"
        >
          Live deployment
        </a>
      )}

      {summary.newerAttempt?.state && (
        <div className="text-xs text-amber-600">Latest attempt: {summary.newerAttempt.state}</div>
      )}

      {(summary.errors || []).map((error, index) => (
        <div key={index} className="text-xs text-destructive">
          {error.provider}: {error.message}
        </div>
      ))}

      <Button size="sm" variant="outline" onClick={onRetry} disabled={retrying}>
        {retrying ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Retry'}
      </Button>
    </div>
  )
}

function FleetTable({ rows, retryingNames, onRetry, selectedClinics, onToggleSelect }) {
  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="p-3 w-8" />
                <th className="text-left p-3 font-medium">Clinic</th>
                <th className="text-left p-3 font-medium">Metadata</th>
                <th className="text-left p-3 font-medium">Environment</th>
                <th className="text-left p-3 font-medium">Deployment</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="p-6 text-center text-muted-foreground">
                    No clinics match this filter.
                  </td>
                </tr>
              ) : (
                rows.map(({ fleetClinic, row }) => {
                  const canDeploy = row.status === 'ready' && row.summary?.project != null
                  return (
                    <tr key={fleetClinic.name} className="border-t align-top hover:bg-muted/30">
                      <td className="p-3">
                        <Checkbox
                          checked={selectedClinics.has(fleetClinic.name)}
                          onCheckedChange={() => onToggleSelect(fleetClinic.name)}
                          disabled={!canDeploy}
                          title={!canDeploy ? 'No Vercel project — cannot deploy' : undefined}
                        />
                      </td>
                      <td className="p-3"><ClinicCell fleetClinic={fleetClinic} summary={row.summary} /></td>
                      <td className="p-3"><MetadataCell row={row} /></td>
                      <td className="p-3"><EnvironmentCell row={row} /></td>
                      <td className="p-3">
                        <DeploymentCell
                          row={row}
                          retrying={retryingNames.has(fleetClinic.name)}
                          onRetry={() => onRetry(fleetClinic.name)}
                        />
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  )
}

function RolloutOutcomesGrid({ outcome, pollError }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Rollout outcomes</CardTitle>
        <CardDescription>
          {outcome.summary.total} clinic{outcome.summary.total === 1 ? '' : 's'} · {outcome.summary.accepted} accepted ·{' '}
          {outcome.summary.blocked} blocked · {outcome.summary.failed} failed
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {pollError && <div className="text-xs text-destructive">{pollError}</div>}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {outcome.results.map((result) => (
            <div key={result.clinic} className="border rounded-md p-3 space-y-1 text-sm">
              <div className="font-medium">{result.clinic}</div>
              <div>
                <Badge variant="outline">{ROLLOUT_STATUS_LABEL[result.status] || result.status}</Badge>
              </div>
              {result.projectName && <div className="text-xs text-muted-foreground">Project: {result.projectName}</div>}
              {result.branch && <div className="text-xs text-muted-foreground">Branch: {result.branch}</div>}
              {result.deploymentId && (
                <div className="text-xs text-muted-foreground break-all">Deployment: {result.deploymentId}</div>
              )}
              {result.acceptedAt && (
                <div className="text-xs text-muted-foreground">Accepted: {formatUtc(result.acceptedAt)}</div>
              )}
              {result.url && (
                <a
                  href={result.url.startsWith('http') ? result.url : `https://${result.url}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-primary hover:underline block"
                >
                  Open in Vercel
                </a>
              )}
              {result.error && <div className="text-xs text-destructive">{result.error}</div>}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

function FleetView() {
  const [fleetClinics, setFleetClinics] = useState([])
  const [rowsByName, setRowsByName] = useState(() => new Map())
  const [refreshedAt, setRefreshedAt] = useState(null)
  const [search, setSearch] = useState('')
  const [activeFilter, setActiveFilter] = useState('All')
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const [vercelUnconfigured, setVercelUnconfigured] = useState(null)
  const [retryingNames, setRetryingNames] = useState(() => new Set())

  const [selectedClinics, setSelectedClinics] = useState(() => new Set())
  const [rolloutPanelOpen, setRolloutPanelOpen] = useState(false)
  const [rolloutOutcome, setRolloutOutcome] = useState(null)
  const [rolloutDispatching, setRolloutDispatching] = useState(false)
  const [rolloutDispatchError, setRolloutDispatchError] = useState(null)
  const [rolloutPollError, setRolloutPollError] = useState(null)

  const generationRef = useRef(0)
  const rolloutPollTimerRef = useRef(null)

  const fetchSummaries = useCallback(async (names, bypassCache, generation) => {
    const chunks = []
    for (let i = 0; i < names.length; i += SUMMARY_BATCH_SIZE) {
      chunks.push(names.slice(i, i + SUMMARY_BATCH_SIZE))
    }

    let next = 0
    const worker = async () => {
      while (next < chunks.length) {
        const chunk = chunks[next++]
        try {
          const res = await expressPortalsAPI.getFleetSummaries(chunk, bypassCache)
          if (generationRef.current !== generation) continue
          const summaries = res.data?.data?.summaries || []
          setRowsByName((prev) => {
            const updated = new Map(prev)
            for (const summary of summaries) {
              updated.set(summary.clinic, { status: 'ready', summary })
            }
            return updated
          })
        } catch (err) {
          if (generationRef.current !== generation) continue
          const message = apiErrorMessage(err, 'Failed to load summary')
          setRowsByName((prev) => {
            const updated = new Map(prev)
            for (const name of chunk) updated.set(name, { status: 'error', error: message })
            return updated
          })
        }
      }
    }

    await Promise.all(Array.from({ length: Math.min(SUMMARY_BATCH_WORKERS, chunks.length) }, worker))
  }, [])

  const loadFleet = useCallback(async (bypassCache) => {
    setLoading(true)
    setLoadError(null)
    setVercelUnconfigured(null)

    try {
      const res = await expressPortalsAPI.getFleet(bypassCache)
      const { clinics, refreshedAt: refreshed } = res.data.data
      setFleetClinics(clinics)
      setRefreshedAt(refreshed)

      const generation = ++generationRef.current
      setRowsByName(new Map(clinics.map((clinic) => [clinic.name, { status: 'loading' }])))

      await fetchSummaries(clinics.map((clinic) => clinic.name), bypassCache, generation)
    } catch (err) {
      if (err?.response?.status === 503) {
        setVercelUnconfigured(apiErrorMessage(err, 'Vercel is not configured.'))
      } else {
        setLoadError(apiErrorMessage(err, err.message))
      }
    } finally {
      setLoading(false)
    }
  }, [fetchSummaries])

  useEffect(() => { loadFleet(false) }, [loadFleet])

  const handleRetry = useCallback(async (name) => {
    const generation = generationRef.current
    setRetryingNames((prev) => new Set(prev).add(name))

    try {
      const res = await expressPortalsAPI.getFleetSummaries([name], true)
      if (generationRef.current === generation) {
        const summary = (res.data?.data?.summaries || []).find((s) => s.clinic === name)
        setRowsByName((prev) => {
          const updated = new Map(prev)
          updated.set(name, summary ? { status: 'ready', summary } : { status: 'error', error: 'Failed to load summary' })
          return updated
        })
      }
    } catch (err) {
      if (generationRef.current === generation) {
        const message = apiErrorMessage(err, 'Failed to load summary')
        setRowsByName((prev) => new Map(prev).set(name, { status: 'error', error: message }))
      }
    } finally {
      setRetryingNames((prev) => {
        const updated = new Set(prev)
        updated.delete(name)
        return updated
      })
    }
  }, [])

  const toggleSelected = useCallback((name) => {
    setSelectedClinics((prev) => {
      const updated = new Set(prev)
      if (updated.has(name)) updated.delete(name)
      else updated.add(name)
      return updated
    })
  }, [])

  const clearRolloutPollTimer = useCallback(() => {
    if (rolloutPollTimerRef.current) {
      clearTimeout(rolloutPollTimerRef.current)
      rolloutPollTimerRef.current = null
    }
  }, [])

  useEffect(() => clearRolloutPollTimer, [clearRolloutPollTimer])

  const scheduleRolloutPoll = useCallback((rolloutId, consecutiveFailures = 0) => {
    clearRolloutPollTimer()
    rolloutPollTimerRef.current = setTimeout(async () => {
      let shouldContinuePolling = false
      let failures = consecutiveFailures

      try {
        const res = await expressPortalsAPI.getRolloutStatus(rolloutId)
        const polled = res.data.data
        failures = 0
        setRolloutPollError(null)
        setRolloutOutcome((prev) => {
          if (!prev || prev.rolloutId !== rolloutId) return prev
          const merged = { ...prev, results: mergeRolloutResults(prev.results, polled.results) }
          shouldContinuePolling = merged.results.some((result) => isRolloutOutcomeActive(result.status))
          return merged
        })
      } catch (err) {
        failures = consecutiveFailures + 1
        const givingUp = failures >= ROLLOUT_POLL_MAX_FAILURES
        setRolloutPollError(
          givingUp
            ? `Stopped checking status after ${failures} failed attempts: ${apiErrorMessage(err, 'request failed')}. The deploys themselves are unaffected — check Vercel for the outcome.`
            : `Monitoring status is temporarily unavailable: ${apiErrorMessage(err, 'request failed')}`
        )
        // On the last failure leave shouldContinuePolling false, so the loop ends
        // here rather than re-arming off results it was never able to refresh.
        if (!givingUp) {
          setRolloutOutcome((prev) => {
            if (!prev || prev.rolloutId !== rolloutId) return prev
            shouldContinuePolling = prev.results.some((result) => isRolloutOutcomeActive(result.status))
            return prev
          })
        }
      }

      if (shouldContinuePolling) scheduleRolloutPoll(rolloutId, failures)
    }, rolloutPollDelay(consecutiveFailures))
  }, [clearRolloutPollTimer])

  const dispatchFleetRollout = useCallback(async () => {
    const clinicIds = [...selectedClinics].map(coreNameToSlug)
    setRolloutDispatching(true)
    setRolloutDispatchError(null)
    setRolloutPollError(null)
    clearRolloutPollTimer()

    try {
      const res = await expressPortalsAPI.dispatchRollout(clinicIds)
      const dispatched = res.data.data
      setRolloutOutcome(dispatched)
      setSelectedClinics(new Set())
      setRolloutPanelOpen(false)
      if (dispatched.results.some((result) => isRolloutOutcomeActive(result.status))) {
        scheduleRolloutPoll(dispatched.rolloutId)
      }
    } catch (err) {
      setRolloutDispatchError(apiErrorMessage(err, 'Unable to dispatch rollout'))
    } finally {
      setRolloutDispatching(false)
    }
  }, [selectedClinics, clearRolloutPollTimer, scheduleRolloutPoll])

  const needle = search.trim().toLowerCase()
  const visibleRows = fleetClinics
    .map((fleetClinic) => ({ fleetClinic, row: rowsByName.get(fleetClinic.name) || { status: 'loading' } }))
    .filter(({ fleetClinic, row }) => {
      if (needle) {
        const matchesName = fleetClinic.name.toLowerCase().includes(needle)
        const matchesBrand = (row.summary?.brandName || '').toLowerCase().includes(needle)
        if (!matchesName && !matchesBrand) return false
      }
      return matchesFilter(activeFilter, row)
    })

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Express Portals</h1>
          <p className="text-sm text-muted-foreground mt-1">Fleet-wide portal health, read-only.</p>
        </div>
        <div className="flex items-center gap-3">
          {refreshedAt && (
            <span className="text-xs text-muted-foreground">Last refreshed: {formatUtc(refreshedAt)}</span>
          )}
          <span className="text-xs text-muted-foreground">{selectedClinics.size} selected</span>
          <Button onClick={() => setRolloutPanelOpen(true)} disabled={selectedClinics.size === 0}>
            Fleet Deploy
          </Button>
          <Button variant="outline" onClick={() => loadFleet(true)} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh
          </Button>
        </div>
      </div>

      <Dialog open={rolloutPanelOpen} onOpenChange={setRolloutPanelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Fleet Deploy</DialogTitle>
            <DialogDescription>
              Dispatch runs through the shared rollout path. This will deploy the following {selectedClinics.size}{' '}
              clinic{selectedClinics.size === 1 ? '' : 's'}:
            </DialogDescription>
          </DialogHeader>
          <ul className="text-sm list-disc pl-5 max-h-48 overflow-y-auto">
            {[...selectedClinics].map((name) => <li key={name}>{name}</li>)}
          </ul>
          {rolloutDispatchError && <div className="text-sm text-destructive">{rolloutDispatchError}</div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRolloutPanelOpen(false)} disabled={rolloutDispatching}>
              Cancel
            </Button>
            <Button onClick={dispatchFleetRollout} disabled={rolloutDispatching}>
              {rolloutDispatching ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Confirm deploy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {rolloutOutcome && <RolloutOutcomesGrid outcome={rolloutOutcome} pollError={rolloutPollError} />}

      {vercelUnconfigured && (
        <Card>
          <CardHeader>
            <CardTitle>Vercel is not configured</CardTitle>
            <CardDescription>{vercelUnconfigured}</CardDescription>
          </CardHeader>
        </Card>
      )}

      {!vercelUnconfigured && loadError && (
        <Card>
          <CardContent className="py-6">
            <div className="text-center text-destructive space-y-3">
              <p>{loadError}</p>
              <Button variant="outline" onClick={() => loadFleet(false)}>Try Again</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {!vercelUnconfigured && !loadError && (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              placeholder="Search clinic or brand"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-xs"
            />
            <div className="flex items-center gap-1 flex-wrap">
              {FILTERS.map((filter) => (
                <button
                  key={filter}
                  onClick={() => setActiveFilter(filter)}
                  className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                    activeFilter === filter
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'text-muted-foreground border-border hover:bg-muted'
                  }`}
                >
                  {filter}
                </button>
              ))}
            </div>
          </div>

          <FleetTable
            rows={visibleRows}
            retryingNames={retryingNames}
            onRetry={handleRetry}
            selectedClinics={selectedClinics}
            onToggleSelect={toggleSelected}
          />
        </>
      )}
    </div>
  )
}

export default function ExpressPortalsPage() {
  const { user, currentClinicId } = useAuthStore()

  if (user?.role !== 'super_admin') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>This page is restricted to super administrators.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  if (currentClinicId !== null) {
    return <ClinicDetailView clinicId={currentClinicId} />
  }

  return <FleetView />
}
